import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, Sparkles, Plus, Trash2, Building2, Video,
  Mail, Lock, User, Eye, EyeOff, Check, ShieldCheck,
} from "lucide-react";
import { authRegister, uploadPortfolio, formatApiError } from "../lib/api";
import { useUser } from "../context/UserContext";
import { GAMES, QUALITIES, SERVICES, LANGUAGES, DELIVERY_SCOPES } from "../lib/constants";
import InfoTip from "../components/InfoTip";
import AvatarUploader from "../components/AvatarUploader";
import usePageMeta from "../lib/usePageMeta";
import { toast } from "sonner";

const COMPANY_GLYPHS = ["◆", "◇", "◈", "◉", "◊", "⬡", "⬢", "△"];
const CREATOR_GLYPHS = ["❄", "⚡", "☾", "◉", "◬", "⟁", "✦", "△", "◈"];

/** Returns { score: 0-3, label, color } */
function passwordStrength(pw) {
  if (!pw) return { score: 0, label: "—", color: "#606070" };
  let s = 0;
  if (pw.length >= 8) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/\d/.test(pw) && /[^A-Za-z0-9]/.test(pw)) s++;
  if (pw.length >= 12) s = Math.max(s, 3);
  const map = [
    { label: "Too short", color: "#ff4444" },
    { label: "Weak", color: "#ff8844" },
    { label: "Medium", color: "#ffaa00" },
    { label: "Strong", color: "#00ff88" },
  ];
  return { score: s, ...map[s] };
}

export default function Signup() {
  usePageMeta({
    title: "Sign Up — CheatCraft",
    description: "Create your CheatCraft account — as a cheat software company or as a media creator — and start trading safely through escrow.",
    canonicalPath: "/signup",
  });
  const nav = useNavigate();
  const { setUser } = useUser();
  const [params] = useSearchParams();
  const initialRole = params.get("role") === "creator" ? "creator" : "company";

  const [step, setStep] = useState(initialRole && params.get("role") ? 2 : 1);
  const [role, setRole] = useState(initialRole);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showPw, setShowPw] = useState(false);

  const [common, setCommon] = useState({
    email: "", username: "", password: "", confirm: "", name: "",
    accept_terms: false,
  });
  const [company, setCompany] = useState({ avatar_url: null, glyph: "◆", website: "", description: "" });
  const [creator, setCreator] = useState({
    avatar_url: null, glyph: "❄", bio: "",
    games: [], services: [], languages: ["EN"],
    quality: "1080p 60fps", has_mic: true, has_facecam: false,
    price: 80, avg_delivery_days: 7, delivery_scope: "record_edit",
    portfolio_urls: [""],
    portfolio_files: [],
  });

  const strength = useMemo(() => passwordStrength(common.password), [common.password]);

  const toggleList = (key, v) =>
    setCreator((f) => ({ ...f, [key]: f[key].includes(v) ? f[key].filter((x) => x !== v) : [...f[key], v] }));
  const setPortfolio = (i, v) =>
    setCreator((f) => { const copy = [...f.portfolio_urls]; copy[i] = v; return { ...f, portfolio_urls: copy }; });
  const addPortfolio = () => setCreator((f) => ({ ...f, portfolio_urls: [...f.portfolio_urls, ""] }));
  const removePortfolio = (i) =>
    setCreator((f) => ({ ...f, portfolio_urls: f.portfolio_urls.filter((_, k) => k !== i) }));

  const pickRole = (r) => { setRole(r); setStep(2); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!common.email || !common.password || !common.username.trim()) {
      return setError("Email, username and password are required.");
    }
    if (common.password.length < 8) return setError("Password must be at least 8 characters.");
    if (common.password !== common.confirm) return setError("Passwords do not match.");
    if (!common.accept_terms) return setError("You must accept the Terms of Service.");
    if (role === "creator" && (creator.games.length === 0 || creator.services.length === 0)) {
      return setError("Pick at least 1 game and 1 service.");
    }

    const displayName = (common.name || common.username).trim();
    const payload =
      role === "company"
        ? {
            email: common.email, username: common.username.trim(), password: common.password,
            name: displayName, role, accept_terms: true, ...company,
          }
        : {
            email: common.email, username: common.username.trim(), password: common.password,
            name: displayName, role, accept_terms: true, ...creator,
            portfolio_urls: creator.portfolio_urls.filter((u) => u.trim()),
          };

    setSaving(true);
    try {
      const u = await authRegister(payload);
      // If the creator dropped any files, upload them now that cookies are set
      // and patch the profile to include the resulting URLs.
      if (role === "creator" && creator.portfolio_files.length > 0 && u.profile_id) {
        const extraUrls = [];
        for (const f of creator.portfolio_files) {
          try {
            const res = await uploadPortfolio(f);
            if (res?.url) extraUrls.push(res.url);
          } catch (_) { /* surface individually? best-effort for now */ }
        }
        if (extraUrls.length > 0) {
          const merged = [
            ...creator.portfolio_urls.filter((x) => x.trim()),
            ...extraUrls,
          ];
          try {
            const { updateCreator } = await import("../lib/api");
            await updateCreator(u.profile_id, { portfolio_urls: merged });
          } catch (_) { /* non-fatal */ }
        }
      }
      setUser(u);
      toast.success(`Welcome, ${u.display_name}`);
      // Send to email-sent confirmation page (verification flow).
      nav("/email-sent", {
        replace: true,
        state: {
          email: u.email,
          dev_verify_token: u.dev_verify_token,
          verify_path: u.verify_path,
          role: u.role,
        },
      });
    } catch (err) {
      setError(formatApiError(err, "Signup failed."));
    } finally {
      setSaving(false);
    }
  };

  // ─── STEP 1 — Role chooser ─────────────────────────────────────────
  if (step === 1) {
    return (
      <div data-testid="signup-page" className="max-w-3xl mx-auto px-6 py-20">
        <div className="text-center mb-10">
          <div className="cc-overline inline-flex items-center gap-2"><Sparkles size={11} /> Create account</div>
          <h1 className="text-3xl md:text-5xl font-semibold tracking-tight text-white mt-3">
            Welcome to <span className="text-[#00ff88]">CHEATCRAFT</span>
          </h1>
          <p className="text-sm text-[#a0a0b0] mt-3">First, tell us who you are.</p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <RoleCard
            testId="role-creator"
            icon={<Video size={22} />}
            title="I'm a Creator"
            sub="Showcase your skills, get hired, earn money."
            badge="🎬"
            onClick={() => pickRole("creator")}
          />
          <RoleCard
            testId="role-company"
            icon={<Building2 size={22} />}
            title="I'm a Company"
            sub="Find creators to promote your software."
            badge="🏢"
            onClick={() => pickRole("company")}
          />
        </div>

        <div className="text-center mt-8 text-xs text-[#a0a0b0]">
          Already have an account?{" "}
          <Link to="/login" data-testid="signup-step1-login-link" className="text-[#00ff88] hover:underline">Log in</Link>
        </div>
      </div>
    );
  }

  // ─── STEP 2 — Form ─────────────────────────────────────────────────
  return (
    <div data-testid="signup-page" className="max-w-2xl mx-auto px-6 py-16">
      <button
        type="button"
        data-testid="signup-back-step1"
        onClick={() => setStep(1)}
        className="text-xs text-[#a0a0b0] hover:text-white inline-flex items-center gap-1 mb-4"
      >
        <ArrowLeft size={12} /> Change role
      </button>

      <div className="cc-overline inline-flex items-center gap-2">
        {role === "company" ? <Building2 size={11} /> : <Video size={11} />}
        Step 2 · {role === "company" ? "Company" : "Creator"} signup
      </div>
      <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-white mt-3 mb-2">
        Create your <span className="text-[#00ff88]">{role}</span> account.
      </h1>
      <p className="text-sm text-[#a0a0b0] mb-6">All set? It only takes a minute.</p>

      <form onSubmit={submit} className="space-y-5">
        <Section title="Account">
          <Field label="Email" required>
            <Input testId="signup-email" type="email" autoComplete="email" icon={<Mail size={14} />}
              value={common.email}
              onChange={(v) => setCommon({ ...common, email: v })}
              placeholder="you@studio.gg"
            />
          </Field>
          <Field label="Username" required hint="Shown on your public profile. Must be unique.">
            <Input testId="signup-username" icon={<User size={14} />}
              value={common.username}
              onChange={(v) => setCommon({ ...common, username: v.replace(/\s+/g, "") })}
              placeholder={role === "company" ? "e.g. aimlabs" : "e.g. shadowstrike"}
            />
          </Field>
          {role === "company" && (
            <Field label="Display name (optional)" hint="Public studio name. Defaults to your username.">
              <Input testId="signup-name"
                value={common.name}
                onChange={(v) => setCommon({ ...common, name: v })}
                placeholder="e.g. AimLabs.gg"
              />
            </Field>
          )}
          <Field label="Password" required>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#606070]"><Lock size={14} /></span>
              <input
                data-testid="signup-password"
                type={showPw ? "text" : "password"}
                autoComplete="new-password"
                value={common.password}
                onChange={(e) => setCommon({ ...common, password: e.target.value })}
                placeholder="Min. 8 characters"
                className="w-full bg-[#06060c] border border-[#15152a] rounded-lg pl-9 pr-10 py-2.5 text-sm text-white placeholder-[#606070] cc-focus-ring"
              />
              <button
                type="button"
                data-testid="signup-password-toggle"
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#606070] hover:text-white"
              >
                {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <StrengthBar strength={strength} testId="signup-password-strength" />
          </Field>
          <Field label="Confirm password" required>
            <Input testId="signup-confirm" type={showPw ? "text" : "password"} icon={<Lock size={14} />}
              value={common.confirm}
              onChange={(v) => setCommon({ ...common, confirm: v })}
              placeholder="Repeat password"
            />
            {common.confirm && common.confirm !== common.password && (
              <div data-testid="signup-confirm-mismatch" className="text-[11px] text-[#ff8888] mt-1">
                Passwords do not match.
              </div>
            )}
          </Field>
        </Section>

        {role === "company" ? (
          <Section title="Studio details">
            <Field label="Website">
              <Input testId="signup-company-website"
                value={company.website}
                onChange={(v) => setCompany({ ...company, website: v })}
                placeholder="https://your-studio.com"
              />
            </Field>
            <Field label="Description">
              <textarea
                data-testid="signup-company-description"
                rows={3}
                value={company.description}
                onChange={(e) => setCompany({ ...company, description: e.target.value })}
                placeholder="What does your studio build?"
                className="w-full bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2.5 text-sm text-white placeholder-[#606070] cc-focus-ring"
              />
            </Field>
            <Field label="Company logo" required>
              <AvatarUploader
                value={company.avatar_url}
                onChange={(url) => setCompany({ ...company, avatar_url: url })}
                fallback="◆"
                testId="signup-company-avatar"
              />
            </Field>
          </Section>
        ) : (
          <>
            <Section title="Creator profile">
              <Field label="Bio">
                <textarea
                  data-testid="signup-creator-bio"
                  rows={3}
                  value={creator.bio}
                  onChange={(e) => setCreator({ ...creator, bio: e.target.value })}
                  placeholder="Short intro for companies."
                  className="w-full bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2.5 text-sm text-white placeholder-[#606070] cc-focus-ring"
                />
              </Field>
              <Field label="Avatar" required>
                <AvatarUploader
                  value={creator.avatar_url}
                  onChange={(url) => setCreator({ ...creator, avatar_url: url })}
                  fallback="❄"
                  testId="signup-creator-avatar"
                />
              </Field>
            </Section>

            <Section title="Games & Services">
              <Field label="Games you record" required>
                <Chips options={GAMES} selected={creator.games} onToggle={(v) => toggleList("games", v)} testPrefix="creator-game" />
              </Field>
              <Field label="Services offered" required>
                <Chips options={SERVICES} selected={creator.services} onToggle={(v) => toggleList("services", v)} testPrefix="creator-service" />
              </Field>
              <Field label="Languages">
                <Chips options={LANGUAGES} selected={creator.languages} onToggle={(v) => toggleList("languages", v)} testPrefix="creator-lang" />
              </Field>
            </Section>

            <Section title="Equipment & Quality">
              <Field label="Video quality">
                <select
                  data-testid="signup-creator-quality"
                  value={creator.quality}
                  onChange={(e) => setCreator({ ...creator, quality: e.target.value })}
                  className="bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2 text-sm text-white cc-focus-ring"
                >
                  {QUALITIES.map((q) => <option key={q} className="bg-[#06060c]">{q}</option>)}
                </select>
              </Field>
              <div className="flex items-center gap-6 mt-3">
                <Toggle label="Microphone" checked={creator.has_mic} onChange={(v) => setCreator({ ...creator, has_mic: v })} testId="signup-creator-mic" />
                <Toggle label="Facecam" checked={creator.has_facecam} onChange={(v) => setCreator({ ...creator, has_facecam: v })} testId="signup-creator-facecam" />
              </div>
            </Section>

            <Section title="Pricing & Delivery">
              <Field label="Base price per video (USD)">
                <div className="flex items-center gap-2">
                  <span className="text-[#00ff88] font-mono">$</span>
                  <input
                    data-testid="signup-creator-price"
                    type="number"
                    value={creator.price}
                    onChange={(e) => setCreator({ ...creator, price: Number(e.target.value) || 0 })}
                    className="w-32 bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2.5 text-sm text-white font-mono cc-focus-ring"
                  />
                </div>
              </Field>
              <Field label="What's included?">
                <div className="grid sm:grid-cols-3 gap-2">
                  {DELIVERY_SCOPES.map((s) => (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => setCreator({ ...creator, delivery_scope: s.key })}
                      data-testid={`signup-scope-${s.key}`}
                      className={`text-left rounded-lg p-3 border transition-colors ${
                        creator.delivery_scope === s.key
                          ? "border-[#00ff88]/40 bg-[#00ff88]/5"
                          : "border-[#15152a] bg-[#06060c] hover:border-[#1f1f3a]"
                      }`}
                    >
                      <div className={`text-sm font-semibold ${creator.delivery_scope === s.key ? "text-[#00ff88]" : "text-white"}`}>
                        {s.label}
                      </div>
                      <div className="text-[11px] text-[#606070] mt-0.5">{s.sub}</div>
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Max delivery time (days)">
                <div className="flex items-center gap-2">
                  <input
                    data-testid="signup-creator-delivery-days"
                    type="number"
                    min="1"
                    max="60"
                    value={creator.avg_delivery_days}
                    onChange={(e) => setCreator({ ...creator, avg_delivery_days: Number(e.target.value) || 1 })}
                    className="w-24 bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2.5 text-sm text-white font-mono cc-focus-ring"
                  />
                  <span className="text-xs text-[#a0a0b0]">days max</span>
                  <InfoTip
                    testId="signup-delivery-tip"
                    text="If you don't deliver within this window, the company can request a full refund. Pick a value you can confidently honour."
                    side="bottom"
                  />
                </div>
              </Field>
              <div className="text-[11px] text-[#606070] mt-1 inline-flex items-center gap-1.5">
                <Sparkles size={11} className="text-[#00ff88]" />
                Tip: you can add custom add-ons (Facecam, Rush, Thumbnail…) from your profile settings after signup.
              </div>
            </Section>

            <Section title="Portfolio">
              <p className="text-[11px] text-[#606070]">
                Drop a video or image (mp4, webm, jpg, png · up to 100 MB each), or paste YouTube URLs. You can add more from your profile later.
              </p>

              <div className="space-y-2">
                {creator.portfolio_files.map((f, i) => (
                  <div key={`f-${i}`} data-testid={`signup-creator-portfolio-file-${i}`} className="flex items-center gap-2 rounded-lg border border-[#00ff88]/30 bg-[#00ff88]/5 px-3 py-2">
                    <span className="text-[11px] text-[#00ff88] font-mono truncate flex-1">📎 {f.name}</span>
                    <span className="text-[10px] text-[#606070]">{(f.size / (1024 * 1024)).toFixed(1)} MB</span>
                    <button
                      type="button"
                      onClick={() => setCreator((c) => ({ ...c, portfolio_files: c.portfolio_files.filter((_, k) => k !== i) }))}
                      className="text-[#ff4444] hover:bg-[#ff4444]/10 rounded p-1"
                      aria-label="Remove file"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}

                <label
                  data-testid="signup-creator-portfolio-dropzone"
                  onDragOver={(e) => { e.preventDefault(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const files = Array.from(e.dataTransfer?.files || []).filter((f) => f.size <= 100 * 1024 * 1024);
                    if (files.length) setCreator((c) => ({ ...c, portfolio_files: [...c.portfolio_files, ...files] }));
                  }}
                  className="flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-[#15152a] bg-[#06060c] hover:border-[#00ff88]/40 transition-colors py-4 cursor-pointer text-sm text-[#a0a0b0]"
                >
                  <input
                    type="file"
                    accept="video/*,image/*"
                    multiple
                    className="hidden"
                    data-testid="signup-creator-portfolio-file-input"
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []).filter((f) => f.size <= 100 * 1024 * 1024);
                      if (files.length) setCreator((c) => ({ ...c, portfolio_files: [...c.portfolio_files, ...files] }));
                      e.target.value = "";
                    }}
                  />
                  <Plus size={14} className="text-[#00ff88]" />
                  Drag files here, or click to browse
                </label>

                <div className="pt-1">
                  <div className="cc-overline mb-1.5">YouTube URLs</div>
                  {creator.portfolio_urls.map((u, i) => (
                    <div key={i} className="flex gap-2 mb-2">
                      <input
                        data-testid={`signup-creator-portfolio-${i}`}
                        value={u}
                        onChange={(e) => setPortfolio(i, e.target.value)}
                        placeholder="https://youtube.com/watch?v=…"
                        className="flex-1 bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2.5 text-sm text-white placeholder-[#606070] font-mono cc-focus-ring"
                      />
                      {creator.portfolio_urls.length > 1 && (
                        <button type="button" onClick={() => removePortfolio(i)} className="px-3 text-[#ff4444] hover:bg-[#ff4444]/10 border border-[#15152a] rounded-lg">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addPortfolio}
                    data-testid="signup-creator-portfolio-add"
                    className="text-xs text-[#00ff88] hover:underline inline-flex items-center gap-1"
                  >
                    <Plus size={12} /> Add another link
                  </button>
                </div>
              </div>
            </Section>
          </>
        )}

        <Section title="Terms">
          <label data-testid="signup-terms" className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={common.accept_terms}
              onChange={(e) => setCommon({ ...common, accept_terms: e.target.checked })}
              data-testid="signup-terms-checkbox"
              className="mt-1 accent-[#00ff88]"
            />
            <span className="text-xs text-[#a0a0b0]">
              I agree to the{" "}
              <a href="#" className="text-[#00ff88] hover:underline">Terms of Service</a>{" "}
              and{" "}
              <a href="#" className="text-[#00ff88] hover:underline">Privacy Policy</a>.
            </span>
          </label>
        </Section>

        {error && (
          <div data-testid="signup-error" className="text-xs text-[#ff8888] bg-[#ff4444]/5 border border-[#ff4444]/30 rounded-md px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex items-center justify-between pt-2">
          <Link to="/login" data-testid="signup-login-link" className="text-xs text-[#a0a0b0] hover:text-white">
            Already have an account? Log in
          </Link>
          <button
            data-testid="signup-submit"
            disabled={saving || !common.accept_terms}
            className="cc-btn-primary rounded-lg px-6 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <><Spinner /> Creating…</>
            ) : (
              <>Create Account <ArrowRight size={14} /></>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

const Spinner = () => (
  <span className="w-3.5 h-3.5 border-2 border-[#06060c]/30 border-t-[#06060c] rounded-full animate-spin" />
);

const StrengthBar = ({ strength, testId }) => (
  <div className="mt-2 flex items-center gap-2" data-testid={testId}>
    <div className="flex-1 h-1.5 bg-[#15152a] rounded-full overflow-hidden">
      <div
        className="h-full transition-all duration-300"
        style={{ width: `${(strength.score / 3) * 100}%`, background: strength.color }}
      />
    </div>
    <span className="text-[11px] font-mono" style={{ color: strength.color }}>{strength.label}</span>
  </div>
);

const RoleCard = ({ icon, title, sub, badge, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="text-left rounded-2xl p-7 border border-[#15152a] bg-[#0a0a14] hover:border-[#00ff88]/40 hover:bg-[#00ff88]/5 transition-all hover:scale-[1.01]"
  >
    <div className="text-3xl mb-3">{badge}</div>
    <div className="flex items-center gap-2 mb-1 text-white">
      {icon}
      <span className="text-lg font-semibold">{title}</span>
    </div>
    <div className="text-sm text-[#a0a0b0] mt-1">{sub}</div>
    <div className="mt-5 inline-flex items-center gap-1 text-xs text-[#00ff88]">
      Continue <ArrowRight size={12} />
    </div>
  </button>
);

const Section = ({ title, children }) => (
  <section className="cc-surface rounded-2xl p-6 space-y-4">
    <div className="cc-overline">{title}</div>
    {children}
  </section>
);

const Field = ({ label, required, hint, children }) => (
  <div>
    <div className="cc-overline mb-1.5">{label}{required && <span className="text-[#00ff88]"> *</span>}</div>
    {children}
    {hint && <div className="text-[11px] text-[#606070] mt-1">{hint}</div>}
  </div>
);

const Input = ({ testId, value, onChange, type = "text", placeholder, autoComplete, icon }) => (
  <div className="relative">
    {icon && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#606070]">{icon}</span>}
    <input
      data-testid={testId}
      type={type}
      autoComplete={autoComplete}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full bg-[#06060c] border border-[#15152a] rounded-lg ${icon ? "pl-9" : "px-3"} pr-3 py-2.5 text-sm text-white placeholder-[#606070] cc-focus-ring`}
    />
  </div>
);

const GlyphPicker = ({ glyphs, value, onChange, prefix }) => (
  <div className="flex flex-wrap gap-1.5">
    {glyphs.map((g) => (
      <button
        key={g}
        type="button"
        onClick={() => onChange(g)}
        data-testid={`${prefix}-${g}`}
        className={`w-9 h-9 rounded-md border text-[#00ff88] text-lg ${value === g ? "border-[#00ff88]/40 bg-[#00ff88]/5" : "border-[#15152a] bg-[#06060c] hover:border-[#1f1f3a]"}`}
      >
        {g}
      </button>
    ))}
  </div>
);

const Chips = ({ options, selected, onToggle, testPrefix }) => (
  <div className="flex flex-wrap gap-1.5">
    {options.map((o) => (
      <button
        key={o}
        type="button"
        onClick={() => onToggle(o)}
        data-testid={`${testPrefix}-${o}`}
        className={`px-2.5 py-1 rounded-md text-[11px] font-mono transition ${
          selected.includes(o)
            ? "bg-[#00ff88]/10 text-[#00ff88] border border-[#00ff88]/30"
            : "border border-[#15152a] text-[#a0a0b0] hover:text-white"
        }`}
      >
        {o}
      </button>
    ))}
  </div>
);

const Toggle = ({ label, checked, onChange, testId }) => (
  <label className="flex items-center gap-2 text-sm text-[#a0a0b0] cursor-pointer" data-testid={testId}>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative w-9 h-5 rounded-full p-0.5 transition-colors ${checked ? "bg-[#00ff88]/30" : "bg-[#15152a]"}`}
    >
      <span className={`block w-4 h-4 rounded-full transition-transform ${checked ? "translate-x-4 bg-[#00ff88]" : "bg-[#606070]"}`} />
    </button>
    {label}
  </label>
);
